// smoothScrolling.js

const EXCLUDED_ANCHOR_SELECTOR = '.reset-cookies, .navActivator, [href*="Nav"], .venobox, .mm-btn';
const SCROLL_EVENT_START = 'smooth-scroll:start';
const SCROLL_EVENT_END = 'smooth-scroll:end';

let reduceMotion = false;
let reduceMotionQuery = null;
let scrollToTopObserver = null;
let scrollToTopSentinel = null;

const supportsMatchMedia = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
if (supportsMatchMedia) {
    reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    reduceMotion = reduceMotionQuery.matches;
    const handleMotionChange = (event) => {
        reduceMotion = event.matches;
        emitSmoothScrollEvent('smooth-scroll:preference', { reduced: reduceMotion });
    };
    if (typeof reduceMotionQuery.addEventListener === 'function') {
        reduceMotionQuery.addEventListener('change', handleMotionChange);
    } else if (typeof reduceMotionQuery.addListener === 'function') {
        reduceMotionQuery.addListener(handleMotionChange);
    }
}

export function getCSSVariableValue(variableName) {
    const value = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
    const numericValue = parseFloat(value);
    return Number.isFinite(numericValue) ? numericValue : 100;
}

const emitSmoothScrollEvent = (name, detail = {}) => {
    if (typeof document === 'undefined' || typeof CustomEvent !== 'function') {
        return;
    }
    document.dispatchEvent(new CustomEvent(name, { detail }));
};

const getScrollBehavior = (explicitBehavior) => {
    if (explicitBehavior) {
        return explicitBehavior;
    }
    return reduceMotion ? 'auto' : 'smooth';
};

export function scrollToTarget(targetId, {updateHash = true, behavior} = {}) {
    const target = document.getElementById(targetId);
    if (!target) {
        return;
    }

    const scrollOffset = getCSSVariableValue('--bs-scrolloffset');
    const targetRect = target.getBoundingClientRect();
    const destination = targetRect.top + window.pageYOffset - scrollOffset;

    const scrollBehavior = getScrollBehavior(behavior);
    emitSmoothScrollEvent(SCROLL_EVENT_START, { targetId, behavior: scrollBehavior });

    window.scrollTo({
        top: Math.max(0, destination),
        behavior: scrollBehavior
    });

    if (updateHash) {
        const expectedHash = `#${targetId}`;
        if (window.location.hash !== expectedHash) {
            history.replaceState(null, '', expectedHash);
        }
    }

    window.requestAnimationFrame(() => {
        emitSmoothScrollEvent(SCROLL_EVENT_END, { targetId, behavior: scrollBehavior });
    });
}

function handleInitialHash() {
    const hash = window.location.hash;
    if (!hash) {
        return;
    }

    const targetId = hash.substring(1);
    if (!targetId) {
        return;
    }

    requestAnimationFrame(() => scrollToTarget(targetId, {updateHash: false}));
}

function shouldHandleAnchor(anchor) {
    if (!anchor) {
        return false;
    }
    if (!anchor.hash) {
        return false;
    }
    if (anchor.matches(EXCLUDED_ANCHOR_SELECTOR)) {
        return false;
    }
    return true;
}

function handleAnchorClick(event) {
    const anchor = event.target.closest('a[href^="#"]');
    if (!shouldHandleAnchor(anchor)) {
        return;
    }

    const targetId = anchor.hash.substring(1);
    if (!targetId) {
        return;
    }

    event.preventDefault();
    scrollToTarget(targetId);
}

export function scrollToTop() {
    const scrollButtons = document.querySelectorAll('.scrollToTop, .BodyScrollToTop, .scrolltop');

    scrollButtons.forEach(btn => {
        btn.addEventListener('click', event => {
            event.preventDefault();
            const behavior = getScrollBehavior();
            emitSmoothScrollEvent(SCROLL_EVENT_START, { targetId: null, source: 'scroll-to-top' });
            window.scrollTo({top: 0, behavior});
            window.requestAnimationFrame(() => emitSmoothScrollEvent(SCROLL_EVENT_END, { targetId: null, behavior }));
        });
    });

    if (scrollButtons.length === 0) {
        return;
    }

    if ('IntersectionObserver' in window && document.body) {
        ensureScrollToTopObserver(scrollButtons);
        return;
    }

    let scrollTimeout = null;
    window.addEventListener('scroll', () => {
        if (scrollTimeout) {
            window.clearTimeout(scrollTimeout);
        }

        scrollTimeout = window.setTimeout(() => {
            const shouldShow = window.pageYOffset > 50;
            scrollButtons.forEach(btn => {
                btn.classList.toggle('visible', shouldShow);
            });
        }, 100);
    }, {passive: true});
}

document.addEventListener('DOMContentLoaded', () => {
    scrollToTop();
    document.addEventListener('click', handleAnchorClick);

    if (document.readyState === 'complete') {
        handleInitialHash();
    } else {
        window.addEventListener('load', handleInitialHash, {once: true});
    }
});

window.addEventListener('hashchange', () => {
    const newHash = window.location.hash;
    if (!newHash) {
        return;
    }

    const targetId = newHash.substring(1);
    if (targetId) {
        requestAnimationFrame(() => scrollToTarget(targetId, {updateHash: false}));
    }
});

const ensureScrollToTopObserver = (buttons) => {
    if (!('IntersectionObserver' in window) || !document.body || buttons.length === 0) {
        return;
    }

    if (!scrollToTopSentinel) {
        scrollToTopSentinel = document.createElement('span');
        scrollToTopSentinel.setAttribute('data-scrolltop-sentinel', '1');
        scrollToTopSentinel.style.position = 'absolute';
        scrollToTopSentinel.style.top = '0';
        scrollToTopSentinel.style.left = '0';
        scrollToTopSentinel.style.width = '1px';
        scrollToTopSentinel.style.height = '1px';
        scrollToTopSentinel.style.pointerEvents = 'none';
        scrollToTopSentinel.style.opacity = '0';
        document.body.insertBefore(scrollToTopSentinel, document.body.firstChild || null);
    }

    if (!scrollToTopObserver) {
        scrollToTopObserver = new IntersectionObserver((entries) => {
            if (!entries || entries.length === 0) {
                return;
            }
            const entry = entries[0];
            const shouldShow = entry ? !entry.isIntersecting : false;
            buttons.forEach(btn => btn.classList.toggle('visible', shouldShow));
        }, {
            root: null,
            rootMargin: '0px 0px -50px 0px',
            threshold: [0, 1],
        });
    }

    scrollToTopObserver.observe(scrollToTopSentinel);
};
